import type { MailIdentityKind, PublicMailIdentity } from '@embertime/shared'
import { Link } from '@tanstack/react-router'
import { AtSign, Check, Lock } from 'lucide-react'
import type { ReactNode } from 'react'
import type { AccountTone } from '../../lib/mail-account-scope'
import { cn } from '../../lib/utils'
import { Dialog } from '../ui/dialog'
import { AccountTile } from './account-badge'
import { dnsOkCount, IdentityKindIcon } from './identity-list'

// Sheet „Von“ in the compose dialog (#839, Mockup „Verfassen – Von“): the
// sender identities of the CURRENT account only, grouped by kind. Picking an
// identity never changes the account; a locked sender (#535/#830/#785) keeps
// its account but may still choose between that account's identities.

type IdentityLike = Pick<
  PublicMailIdentity,
  | 'id'
  | 'accountId'
  | 'kind'
  | 'address'
  | 'displayName'
  | 'status'
  | 'isDefault'
  | 'dnsSpf'
  | 'dnsDkim'
  | 'dnsDmarc'
  | 'testOk'
>

export interface FromSheetGroup<T extends IdentityLike = IdentityLike> {
  kind: MailIdentityKind
  title: string
  identities: T[]
}

const GROUP_TITLES: Record<MailIdentityKind, string> = {
  own_domain: 'Eigene Domain',
  external_smtp: 'Fremder Postausgang',
}

/** Active identities of this account, own domain first; empty groups dropped.
 * The API order (default first) is kept inside a group. */
export function fromSheetGroups<T extends IdentityLike>(
  identities: readonly T[],
  accountId: string,
): FromSheetGroup<T>[] {
  const usable = identities.filter((i) => i.accountId === accountId && i.status === 'active')
  return (['own_domain', 'external_smtp'] as const)
    .map((kind) => ({
      kind,
      title: GROUP_TITLES[kind],
      identities: usable.filter((i) => i.kind === kind),
    }))
    .filter((group) => group.identities.length > 0)
}

/** True when the compose dialog may switch to this identity: active, and of the
 * current account whenever the sender is locked (another account's identity
 * would be a change of account). */
export function canPickIdentity(
  identity: Pick<IdentityLike, 'accountId' | 'status'>,
  ctx: { accountId: string; senderLocked: boolean },
): boolean {
  if (identity.status !== 'active') return false
  return !ctx.senderLocked || identity.accountId === ctx.accountId
}

/** The account's active default identity, if any. */
export function defaultIdentity<T extends IdentityLike>(
  identities: readonly T[],
  accountId: string,
): T | undefined {
  return identities.find((i) => i.accountId === accountId && i.status === 'active' && i.isDefault)
}

/** Preselection: the stored identity when still active and of this account,
 * else the default identity, else none (= the account address). A `pinned`
 * stored id (a mail from the outbox, #785) is kept even when it is gone or
 * disabled: that mail must never leave through another sender unasked — the
 * dialog shows it as unavailable and the server refuses it (404/409). */
export function pickInitialIdentity(
  identities: readonly IdentityLike[],
  accountId: string,
  storedId?: string | null,
  pinned = false,
): string | undefined {
  if (storedId) {
    const stored = identities.find((i) => i.id === storedId)
    if (stored && stored.accountId === accountId && stored.status === 'active') return stored.id
    if (pinned) return storedId
  }
  return defaultIdentity(identities, accountId)?.id
}

/** The chosen identity id is set but not an active identity of the loaded
 * list (deleted or disabled meanwhile): the compose dialog asks for a pick. */
export function identityUnavailable(
  identities: readonly IdentityLike[] | undefined,
  identityId: string | undefined,
): boolean {
  if (!identities || !identityId) return false
  return !identities.some((i) => i.id === identityId && i.status === 'active')
}

/** identityId the compose dialog sends. `choice` is an explicit pick in the
 * sheet (undefined id = account address); it only counts for its account, so a
 * change of account falls back to the new account's default. Without a loaded
 * list (offline, older server) the pick or the stored id is kept, never guessed. */
export function resolveComposeIdentity(opts: {
  identities: readonly IdentityLike[] | undefined
  accountId: string
  stored: {
    accountId: string
    identityId: string | null | undefined
    /** From the outbox: keep the id even when it is gone (see pickInitialIdentity). */
    pinned?: boolean
  } | null
  choice: { accountId: string; identityId: string | undefined } | null
}): string | undefined {
  const { identities, accountId } = opts
  const choice = opts.choice?.accountId === accountId ? opts.choice : null
  const storedId =
    opts.stored?.accountId === accountId ? (opts.stored.identityId ?? undefined) : undefined
  if (!identities) return choice ? choice.identityId : storedId
  if (choice) {
    if (choice.identityId === undefined) return undefined
    const picked = identities.find((i) => i.id === choice.identityId)
    if (picked && picked.accountId === accountId && picked.status === 'active') return picked.id
    // An explicit pick that is gone meanwhile (disabled in another tab) is not
    // silently swapped for another sender either.
    if (!picked || picked.accountId === accountId) return choice.identityId
  }
  return pickInitialIdentity(identities, accountId, storedId, opts.stored?.pinned === true)
}

/** Warning after the address: DNS not complete, or SMTP login not tested. */
export function identityWarning(identity: IdentityLike): string | null {
  if (identity.kind === 'own_domain') {
    const count = dnsOkCount(identity)
    if (count === null) return 'DNS ungeprüft'
    if (count === 3) return null
    // "fehlt" only for an actually missing record; 'invalid' (malformed) and
    // 'unknown' (timed-out lookup) fall back to the generic count (#839 Abnahme).
    if (count === 2 && identity.dnsDmarc === 'missing') return 'DMARC fehlt'
    return `DNS ${count}/3`
  }
  if (identity.testOk === true) return null
  return identity.testOk === false ? 'Test fehlgeschlagen' : 'nicht getestet'
}

/** Second line of a row: address · Standard · warning. */
export function identityLine(identity: IdentityLike): string {
  const parts = [identity.address]
  if (identity.isDefault) parts.push('Standard')
  const warning = identityWarning(identity)
  if (warning) parts.push(warning)
  return parts.join(' · ')
}

export interface FromSheetAccount {
  id: string
  label: string
  email: string
  disabledAt: string | null
  protected?: boolean
  tone: AccountTone
}

function Row({
  selected,
  disabled,
  icon,
  title,
  line,
  warn,
  onClick,
}: {
  selected: boolean
  disabled?: boolean
  icon: ReactNode
  title: string
  line: string
  warn?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-h-[52px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        selected ? 'bg-accent/15' : 'hover:bg-bg-overlay',
      )}
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-sm font-medium',
            selected ? 'text-accent' : 'text-text-primary',
          )}
        >
          {title}
        </span>
        <span
          className={cn(
            'block truncate text-xs',
            selected ? 'text-accent/80' : warn ? 'text-warning' : 'text-text-tertiary',
          )}
        >
          {line}
        </span>
      </span>
      {selected && <Check className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />}
    </button>
  )
}

export function FromIdentitySheet({
  open,
  account,
  accounts,
  identities,
  loading,
  selectedId,
  senderLocked,
  disabled,
  onClose,
  onPickAccount,
  onPickIdentity,
  onManage,
}: {
  open: boolean
  account: Omit<FromSheetAccount, 'tone'> | undefined
  /** All accounts of the compose dialog; a picker shows when there are several. */
  accounts: readonly FromSheetAccount[]
  identities: readonly PublicMailIdentity[] | undefined
  loading: boolean
  selectedId: string | undefined
  senderLocked: boolean
  disabled?: boolean
  onClose: () => void
  onPickAccount: (accountId: string) => void
  /** undefined = the account address (only offered without a default identity). */
  onPickIdentity: (identityId: string | undefined) => void
  /** „Absender verwalten“ leaves the dialog: save the draft first. */
  onManage: () => void
}) {
  const accountId = account?.id ?? ''
  const list = identities ?? []
  const groups = fromSheetGroups(list, accountId)
  const hasDefault = !!defaultIdentity(list, accountId)
  const ctx = { accountId, senderLocked }
  const showAccounts = !senderLocked && accounts.length > 1
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Von"
      description={account ? `Absender von „${account.label}“` : undefined}
    >
      <div className="flex flex-col gap-1 pt-1">
        {showAccounts && (
          <>
            <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-text-tertiary">
              Konto
            </p>
            {accounts.map((a) => (
              <Row
                key={a.id}
                selected={a.id === accountId}
                disabled={disabled || !!a.disabledAt}
                icon={<AccountTile label={a.label} tone={a.tone} />}
                title={a.label}
                line={`${a.email}${a.protected ? ' · Geschützt' : ''}${a.disabledAt ? ' · deaktiviert' : ''}`}
                onClick={() => {
                  if (a.id !== accountId) onPickAccount(a.id)
                }}
              />
            ))}
            <div className="my-1 h-px bg-border-subtle" />
          </>
        )}
        {account && !hasDefault && (
          <Row
            selected={selectedId === undefined}
            disabled={disabled}
            icon={
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-bg-overlay text-text-secondary">
                <AtSign className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
            }
            title={account.label}
            line={`${account.email} · Kontoadresse`}
            onClick={() => onPickIdentity(undefined)}
          />
        )}
        {loading && (
          <p className="px-3 py-2 text-xs text-text-tertiary">Absender werden geladen …</p>
        )}
        {groups.map((group) => (
          <div key={group.kind} className="flex flex-col gap-1">
            <p className="px-3 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wider text-text-tertiary">
              {group.title}
            </p>
            {group.identities.map((identity) => (
              <Row
                key={identity.id}
                selected={selectedId === identity.id}
                disabled={disabled || !canPickIdentity(identity, ctx)}
                icon={<IdentityKindIcon kind={identity.kind} />}
                title={identity.displayName || identity.address}
                line={identityLine(identity)}
                warn={identityWarning(identity) !== null}
                onClick={() => {
                  if (canPickIdentity(identity, ctx)) onPickIdentity(identity.id)
                }}
              />
            ))}
          </div>
        ))}
        <p className="mt-2 flex gap-2 px-3 text-xs text-text-tertiary">
          <Lock className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Antworten auf Mails aus geschützten Ordnern gehen nur über Absender dieses Kontos.
        </p>
        <div className="my-1 h-px bg-border-subtle" />
        <Link
          to="/settings/mail-accounts"
          onClick={onManage}
          className="flex min-h-12 items-center justify-center rounded-xl px-3 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-overlay hover:text-text-primary"
        >
          Absender verwalten
        </Link>
      </div>
    </Dialog>
  )
}
